import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeString } from '../utils/security';
import { formatMesafeKm } from '../utils/mesafeFormat';
import { 
  fetchAppointmentDetail,
  fetchAppointmentDetailed,
  fetchAssignmentHistory,
  fetchRandevuDetayLink,
  fetchRandevuFormuMeta,
  openRandevuFormuPdf,
  assignTechServiceToAppointment,
  assignTechnicianToAppointment,
  cancelTechnicianFromAppointment,
  cancelTechServiceFromAppointment,
  updateAppointmentStatusManual
} from '../services/serviceRecordsService';
import { fetchTechServices } from '../services/techServicesService';
import { fetchCities, fetchDistrictsByCity } from '../services/locationsService';
import { fetchHakedisByRandevuId } from '../services/hakedisService';
import { fetchRandevuBedelleri } from '../services/randevuBedeliService';
import { resolveRandevuBedeliGosterim } from '../utils/randevuBedeliGosterimHelper';
import { resolveOdemeDurumuGosterim } from '../utils/odemeDurumuGosterimHelper';
import { getApiBaseUrl } from '../services/apiClient';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import AssignmentActionsPanel from '../components/AssignmentActionsPanel';
import ServisKaydiBaglantiKart from '../components/ServisKaydiBaglantiKart';
import TechnicianSelectModal from '../components/TechnicianSelectModal';
import AssignmentHistoryTimeline from '../components/AssignmentHistoryTimeline';
import AppointmentFullDetailView from '../components/AppointmentFullDetailView';
import AppointmentCompletionMap from '../components/AppointmentCompletionMap';
import './ServiceRecordUpdatePage.css';

const safeText = (value) => sanitizeString(value ?? '');

const absoluteUploadUrl = (relativeOrAbsolute) => {
  if (!relativeOrAbsolute || typeof relativeOrAbsolute !== 'string') return null;
  const u = relativeOrAbsolute.trim();
  if (u.startsWith('http')) return u;
  const base = getApiBaseUrl().replace(/\/$/, '');
  const path = u.startsWith('/') ? u : `/${u}`;
  return `${base}${path}`;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  let message = error?.data?.mesaj || error?.data?.message || error?.data?.error || error?.data?.hata || error?.message;
  if (error?.status === 404) return message || 'Randevu bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';
  return message || fallback;
};

const getRandevuBedeliDurumMeta = (durum) => {
  if (durum === 'Onaylandi') {
    return { label: 'Onaylandı', chipClass: 'status-success' };
  }
  if (durum === 'OnayBekliyor') {
    return { label: 'Onay Bekliyor', chipClass: 'status-warning' };
  }
  if (durum === 'HakediseAktarildi') {
    return { label: 'Hakedişe Aktarıldı', chipClass: 'status-info' };
  }
  if (!durum) return null;
  return { label: durum, chipClass: 'status-info' };
};

const getOdemeDurumuGosterim = resolveOdemeDurumuGosterim;

const getTeknikServisDisplayName = (appointment) => {
  if (appointment.teknikServis?.teknikServisAdi) {
    return appointment.teknikServis.teknikServisAdi;
  }
  if (appointment.teknikServisId?.ad || appointment.teknikServisId?.name) {
    const ad = appointment.teknikServisId.ad || appointment.teknikServisId.name;
    const kod = appointment.teknikServisId.kod;
    return kod ? `${ad} (${kod})` : ad;
  }
  if (appointment.teknikServis?.ad || appointment.teknikServis?.name) {
    const ad = appointment.teknikServis.ad || appointment.teknikServis.name;
    const kod = appointment.teknikServis.kod;
    return kod ? `${ad} (${kod})` : ad;
  }
  return '—';
};

const AppointmentDetailPage = () => {
  const { confirm, promptText } = useConfirmDialog();
  const { servisKaydiId, randevuId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const goBack = useBackRoute(servisKaydiId ? `/service-records/${servisKaydiId}/update` : '/service-records');
  const [appointment, setAppointment] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [techServices, setTechServices] = useState([]);
  const [selectedTechServiceId, setSelectedTechServiceId] = useState('');
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [techServicesLoading, setTechServicesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('record');
  const [appointmentContent, setAppointmentContent] = useState(null);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
 // Cache için: { index: blobUrl }
  
  // Detaylı rapor için state'ler
  const [detailedData, setDetailedData] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  
  // Hakediş state'leri
  const [hakedis, setHakedis] = useState(null);
  const [hakedisLoading, setHakedisLoading] = useState(false);
  const [randevuBedeliTutar, setRandevuBedeliTutar] = useState(null);
  const [randevuBedeliDurum, setRandevuBedeliDurum] = useState(null);
  const [randevuBedeliAltEtiket, setRandevuBedeliAltEtiket] = useState(null);
  const [randevuBedeliLoading, setRandevuBedeliLoading] = useState(false);

  // Servis kaydı detay raporu linki (müşteriye giden SMS linki)
  const [detayLink, setDetayLink] = useState(null);
  const [detayLinkLoading, setDetayLinkLoading] = useState(false);
  const [detayLinkError, setDetayLinkError] = useState('');

  const [randevuFormuMeta, setRandevuFormuMeta] = useState(null);
  const [randevuFormuLoading, setRandevuFormuLoading] = useState(false);
  const [randevuFormuPdfLoading, setRandevuFormuPdfLoading] = useState(false);
  
  // Atama yönetimi state'leri (Prompt#43)
  const [showTechnicianModal, setShowTechnicianModal] = useState(false);
  const isAdmin = user?.rol === 'admin' || user?.rol === 'sales';
  
  // appointmentStatus'u erken tanımla (useEffect'lerde kullanılabilmesi için)
  const appointmentStatus = appointment?.durum || '---';

  // Randevu detayını yükle
  const loadAppointmentDetail = useCallback(async () => {
    if (!randevuId) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await fetchAppointmentDetail(randevuId);
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || 'Randevu detayı yüklenemedi');
      }
      
      const appointmentData = response?.veri || response?.data || response;
      setAppointment(appointmentData);
      
      // Atama geçmişini yükle
      try {
        const historyResponse = await fetchAssignmentHistory(randevuId);
        // API response: { basarili: true, veri: { atamaGecmisi: [...] } }
        const history = historyResponse?.veri?.atamaGecmisi || 
                       historyResponse?.data?.atamaGecmisi || 
                       historyResponse?.veri || 
                       historyResponse?.data || 
                       [];
        setAssignmentHistory(Array.isArray(history) ? history : []);
      } catch (historyErr) {
        setAssignmentHistory([]);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Randevu detayı yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [randevuId]);

  useEffect(() => {
    loadAppointmentDetail();
  }, [loadAppointmentDetail]);

  // Servis kaydı detay raporu linkini otomatik yükle (Randevu Tamamlandı ise)
  useEffect(() => {
    const loadDetayLink = async () => {
      if (appointment?.durum !== 'Randevu Tamamlandı' || !randevuId) {
        setDetayLink(null);
        setDetayLinkError('');
        setDetayLinkLoading(false);
        return;
      }
      setDetayLinkLoading(true);
      setDetayLinkError('');
      try {
        const response = await fetchRandevuDetayLink(randevuId);
        if (response?.basarili && response?.veri) {
          const veri = response.veri;
          const link = veri?.kisaLink || veri?.tamLink;
          setDetayLink(link || null);
          if (!link) setDetayLinkError('Link bulunamadı');
        } else {
          setDetayLink(null);
          setDetayLinkError(response?.mesaj || 'Link alınamadı');
        }
      } catch (err) {
        setDetayLink(null);
        setDetayLinkError(getErrorMessage(err, 'Link yüklenemedi'));
      } finally {
        setDetayLinkLoading(false);
      }
    };
    loadDetayLink();
  }, [randevuId, appointment?.durum]);

  useEffect(() => {
    const loadFormMeta = async () => {
      if (appointment?.durum !== 'Randevu Tamamlandı' || !randevuId) {
        setRandevuFormuMeta(null);
        setRandevuFormuLoading(false);
        return;
      }
      setRandevuFormuLoading(true);
      try {
        const response = await fetchRandevuFormuMeta(randevuId);
        if (response?.basarili && response?.veri) {
          setRandevuFormuMeta(response.veri);
        } else {
          setRandevuFormuMeta(null);
        }
      } catch {
        setRandevuFormuMeta(null);
      } finally {
        setRandevuFormuLoading(false);
      }
    };
    loadFormMeta();
  }, [randevuId, appointment?.durum]);

  const handleOpenRandevuFormuPdf = async () => {
    if (!randevuId) return;
    setRandevuFormuPdfLoading(true);
    try {
      await openRandevuFormuPdf(randevuId);
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Randevu formu açılamadı'));
    } finally {
      setRandevuFormuPdfLoading(false);
    }
  };

  // Hakediş bilgisini yükle
  useEffect(() => {
    const loadHakedis = async () => {
      const currentStatus = appointment?.durum || '---';
      if (!randevuId || currentStatus !== 'Randevu Tamamlandı') {
        setHakedis(null);
        return;
      }

      setHakedisLoading(true);
      try {
        const response = await fetchHakedisByRandevuId(randevuId);
        if (response?.basarili && response?.veri?.hakedis) {
          setHakedis(response.veri.hakedis);
        } else {
          setHakedis(null);
        }
      } catch (error) {
        setHakedis(null);
      } finally {
        setHakedisLoading(false);
      }
    };

    loadHakedis();
  }, [randevuId, appointment]);

  // Randevu bedeli (Randevu Bilgileri satırı)
  useEffect(() => {
    const loadRandevuBedeli = async () => {
      if (appointment?.durum !== 'Randevu Tamamlandı' || !randevuId) {
        setRandevuBedeliTutar(null);
        setRandevuBedeliDurum(null);
        setRandevuBedeliAltEtiket(null);
        return;
      }
      setRandevuBedeliLoading(true);
      try {
        const response = await fetchRandevuBedelleri({ randevuId, limit: 1 });
        const list = response?.basarili && response?.veri?.randevuBedelleri
          ? response.veri.randevuBedelleri
          : [];
        const kayit = list[0];
        const gosterim = resolveRandevuBedeliGosterim(appointment, kayit || null);
        setRandevuBedeliTutar(gosterim.toplamTutar != null ? parseFloat(gosterim.toplamTutar) : null);
        setRandevuBedeliDurum(gosterim.durum || null);
        setRandevuBedeliAltEtiket(gosterim.altEtiket || null);
      } catch (err) {
        const gosterim = resolveRandevuBedeliGosterim(appointment, null);
        setRandevuBedeliTutar(gosterim.toplamTutar != null ? parseFloat(gosterim.toplamTutar) : null);
        setRandevuBedeliDurum(gosterim.durum || null);
        setRandevuBedeliAltEtiket(gosterim.altEtiket || null);
      } finally {
        setRandevuBedeliLoading(false);
      }
    };
    loadRandevuBedeli();
  }, [appointment?.durum, appointment?.garantiDahilinde, appointment?.garantiSartlariSaglanmadi, appointment?.servisBedeli, appointment?.musteriTahsilatToplam, randevuId]);

  // Detaylı rapor verilerini yükle (detail tab'ı için)
  useEffect(() => {
    const loadDetailedData = async () => {
      if (activeTab !== 'detail' || !randevuId) {
        return;
      }

      setDetailedLoading(true);
      try {
        const response = await fetchAppointmentDetailed(randevuId);
        if (response?.basarili) {
          setDetailedData(response.veri);
        }
      } catch (error) {
        setDetailedData(null);
      } finally {
        setDetailedLoading(false);
      }
    };

    loadDetailedData();
  }, [activeTab, randevuId]);

  // Atama fonksiyonları (Prompt#43)
  const handleAssignTechServiceFromPanel = () => {
    // Sayfa başına scroll et ve mevcut teknik servis atama formunu göster
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleAssignTechnician = async () => {
    setShowTechnicianModal(true);
  };

  const handleTechnicianSubmit = async (data) => {
    try {
      const response = await assignTechnicianToAppointment(
        servisKaydiId,
        randevuId,
        data
      );
      
      if (response?.basarili !== false) {
        if (window.showNotification) {
          window.showNotification('success', 'Teknisyen başarıyla atandı');
        }
        // Randevu detayını yeniden yükle
        await loadAppointmentDetail();
      } else {
        throw new Error(response?.mesaj || 'Teknisyen ataması başarısız');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Teknisyen atanamadı');
      if (window.showNotification) {
        window.showNotification('error', errorMsg);
      }
      throw err; // Modal'ın kendi hata yönetimi için
    }
  };

  const handleCancelTechnician = async () => {
    const reason = await promptText({
      title: 'Teknisyen iptal gerekçesi',
      message: 'İptal gerekçesini yazın.',
      placeholder: 'Gerekçe',
      minLength: 1,
      multiline: true,
    });
    if (reason === null) return;

    const ok = await confirm({
      title: 'Teknisyen atamasını iptal et',
      message: 'Teknisyen ataması iptal edilecek. Devam etmek istiyor musunuz?',
      variant: 'warning',
      confirmLabel: 'Evet',
    });
    if (!ok) return;

    try {
      const response = await cancelTechnicianFromAppointment(
        servisKaydiId,
        randevuId,
        reason
      );

      if (response?.basarili !== false) {
        if (window.showNotification) {
          window.showNotification('success', 'Teknisyen ataması iptal edildi');
        }
        await loadAppointmentDetail();
      } else {
        throw new Error(response?.mesaj || 'İptal işlemi başarısız');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Teknisyen iptal edilemedi');
      if (window.showNotification) {
        window.showNotification('error', errorMsg);
      }
    }
  };

  const handleCancelTechService = async () => {
    const reason = await promptText({
      title: 'Teknik servis iptal gerekçesi',
      message: 'İptal gerekçesini yazın.',
      placeholder: 'Gerekçe',
      minLength: 1,
      multiline: true,
    });
    if (reason === null) return;

    const ok = await confirm({
      title: 'Teknik servis atamasını iptal et',
      message: 'Teknik servis ataması iptal edilecek. Devam etmek istiyor musunuz?',
      variant: 'warning',
      confirmLabel: 'Evet',
    });
    if (!ok) return;

    try {
      const response = await cancelTechServiceFromAppointment(
        servisKaydiId,
        randevuId,
        reason
      );

      if (response?.basarili !== false) {
        if (window.showNotification) {
          window.showNotification('success', 'Teknik servis ataması iptal edildi');
        }
        await loadAppointmentDetail();
      } else {
        throw new Error(response?.mesaj || 'İptal işlemi başarısız');
      }
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Teknik servis iptal edilemedi');
      if (window.showNotification) {
        window.showNotification('error', errorMsg);
      }
    }
  };


  // Randevu içeriğini yükle (sadece tamamlanmış randevular için)
  useEffect(() => {
    if (!appointment || activeTab !== 'content' || !randevuId) return;
    
    const appointmentStatus = appointment.durum || '---';
    if (appointmentStatus === 'Randevu Tamamlandı') {
      const loadAppointmentContent = async () => {
        setContentLoading(true);
        try {
          // Debug: Backend'den gelen appointment verisini logla
          
          // Önce appointment objesinden direkt veriyi çek
          const yapilanIslemler = appointment.yapilanIslemler || [];
          const degistirilenParcalar = appointment.degistirilenParcalar || [];
          
          // Mesafe bilgisini backend'den al (yeni rotaBilgileri veya eski mesafe)
          const mesafeKm = appointment.rotaBilgileri?.mesafe || appointment.mesafe || null;
          const mesafeGidisDonus = appointment.mesafeGidisDonus === true;
          const rotaSure = appointment.rotaBilgileri?.sure || null;
          
          // Konum mührü (tamamlanmaKonumu) ve teknik servis konumu
          const tamamlanmaKonumu = appointment.tamamlanmaKonumu || null;
          const konumMuhru = tamamlanmaKonumu ? {
            enlem: tamamlanmaKonumu.latitude || tamamlanmaKonumu.enlem,
            boylam: tamamlanmaKonumu.longitude || tamamlanmaKonumu.boylam,
            tarih: tamamlanmaKonumu.tamamlanmaTarihi || tamamlanmaKonumu.tarih,
            adres: tamamlanmaKonumu.adres || appointment.servisKaydi?.adres?.adres || serviceRecord?.adres?.adres || '—'
          } : null;
          
          // Teknik servis konumunu al (teknisyenin bağlı olduğu teknik servisten)
          let teknikServisKonumu = null;
          
          // Önce teknisyen objesindeki techService'i kontrol et (backend'den direkt geliyor)
          if (appointment.teknisyen && 
              appointment.teknisyen.techService &&
              typeof appointment.teknisyen.techService === 'object') {
            // Backend'den direkt gelen techService
            const ts = appointment.teknisyen.techService;
            if (ts.konum && ts.konum.koordinat && 
                ts.konum.koordinat.enlem !== null && ts.konum.koordinat.enlem !== undefined &&
                ts.konum.koordinat.boylam !== null && ts.konum.koordinat.boylam !== undefined) {
              teknikServisKonumu = {
                enlem: parseFloat(ts.konum.koordinat.enlem),
                boylam: parseFloat(ts.konum.koordinat.boylam),
                adres: ts.adres || '—',
                ad: ts.ad || ts.name || 'Teknik Servis',
                kod: ts.kod || null
              };
            }
          }
          
          // Eğer direkt techService yoksa, teknisyenId içindeki techService'i kontrol et (eski format)
          if (!teknikServisKonumu && appointment.teknisyen && 
              appointment.teknisyen.teknisyenId && 
              typeof appointment.teknisyen.teknisyenId === 'object' &&
              appointment.teknisyen.teknisyenId.techService &&
              typeof appointment.teknisyen.teknisyenId.techService === 'object') {
            // Populate edilmiş techService (eski format)
            const ts = appointment.teknisyen.teknisyenId.techService;
            if (ts.konum && ts.konum.koordinat && 
                ts.konum.koordinat.enlem !== null && ts.konum.koordinat.enlem !== undefined &&
                ts.konum.koordinat.boylam !== null && ts.konum.koordinat.boylam !== undefined) {
              teknikServisKonumu = {
                enlem: parseFloat(ts.konum.koordinat.enlem),
                boylam: parseFloat(ts.konum.koordinat.boylam),
                adres: ts.adres || '—',
                ad: ts.ad || ts.name || 'Teknik Servis',
                kod: ts.kod || null
              };
            }
          }
          
          // Eğer teknisyenin techService'inden bulunamadıysa, randevunun teknikServisId'sinden dene
          if (!teknikServisKonumu && appointment.teknikServisId && typeof appointment.teknikServisId === 'object') {
            // Populate edilmiş teknikServisId
            const ts = appointment.teknikServisId;
            if (ts.konum && ts.konum.koordinat &&
                ts.konum.koordinat.enlem !== null && ts.konum.koordinat.enlem !== undefined &&
                ts.konum.koordinat.boylam !== null && ts.konum.koordinat.boylam !== undefined) {
              teknikServisKonumu = {
                enlem: parseFloat(ts.konum.koordinat.enlem),
                boylam: parseFloat(ts.konum.koordinat.boylam),
                adres: ts.adres || '—',
                ad: ts.ad || ts.name || 'Teknik Servis',
                kod: ts.kod || null
              };
            }
          } else if (!teknikServisKonumu && appointment.teknikServis) {
            // Eski format veya direkt teknikServis objesi
            const enlem = appointment.teknikServis.enlem || appointment.teknikServis.latitude || 
                         (appointment.teknikServis.konum && appointment.teknikServis.konum.koordinat ? appointment.teknikServis.konum.koordinat.enlem : null);
            const boylam = appointment.teknikServis.boylam || appointment.teknikServis.longitude || 
                          (appointment.teknikServis.konum && appointment.teknikServis.konum.koordinat ? appointment.teknikServis.konum.koordinat.boylam : null);
            if (enlem !== null && enlem !== undefined && boylam !== null && boylam !== undefined) {
              teknikServisKonumu = {
                enlem: parseFloat(enlem),
                boylam: parseFloat(boylam),
                adres: appointment.teknikServis.adres || appointment.teknikServis.address || '—',
                ad: appointment.teknikServis.ad || appointment.teknikServis.name || 'Teknik Servis',
                kod: appointment.teknikServis.kod || null
              };
            }
          }
          
          // Fotoğrafları al
          const photos = appointment.photos || [];
          
          setAppointmentContent({
            yapilanIslemler,
            degistirilenParcalar,
            mesafeKm,
            mesafeGidisDonus,
            konumMuhru,
            teknikServisKonumu,
            photos,
            // Yeni Routes API bilgileri
            rotaBilgileri: appointment.rotaBilgileri || null,
            rotaSure,
            tamamlanmaAdresi: appointment.tamamlanmaAdresi || null,
            // Garanti bilgisi
            garantiDahilinde: appointment.garantiDahilinde !== undefined ? appointment.garantiDahilinde : null,
            odemeDurumu: appointment.odemeDurumu || null,
            odemeProblemli: appointment.odemeProblemli === true,
            musteriTahsilatToplam: appointment.musteriTahsilatToplam ?? null,
            garantiSartlariSaglanmadi: appointment.garantiSartlariSaglanmadi === true,
            servisBedeli: appointment.servisBedeli ?? null,
            odemeAlinamadiTamamlama: appointment.odemeAlinamadiTamamlama === true,
            teknisyenNotu: appointment.teknisyenNotu || null,
            musteriIcinTeknisyenNotu: appointment.musteriIcinTeknisyenNotu || null,
          });
          
          
          // Mesafe bilgisi için rota oluştur (harita için)
          if (konumMuhru && teknikServisKonumu && konumMuhru.enlem && konumMuhru.boylam && teknikServisKonumu.enlem && teknikServisKonumu.boylam) {
            const mesafeBilgisi = {
              rota: {
                baslangic: {
                  enlem: parseFloat(teknikServisKonumu.enlem),
                  boylam: parseFloat(teknikServisKonumu.boylam),
                  adres: teknikServisKonumu.adres || `${teknikServisKonumu.ad || 'Teknik Servis'}`,
                  kod: teknikServisKonumu.kod || null
                },
                bitis: {
                  enlem: parseFloat(konumMuhru.enlem),
                  boylam: parseFloat(konumMuhru.boylam),
                  adres: konumMuhru.adres || 'Randevu Konumu'
                }
              },
              mesafe: mesafeKm ? {
                kilometre: mesafeKm,
                metre: Math.round(mesafeKm * 1000),
                mil: mesafeKm * 0.621371
              } : null
            };
            setDistanceInfo(mesafeBilgisi);
          } else {
            setDistanceInfo(null);
          }
        } catch (err) {
          setAppointmentContent(null);
          setDistanceInfo(null);
        } finally {
          setContentLoading(false);
        }
      };
      loadAppointmentContent();
    }
  }, [activeTab, appointment, randevuId]);

  // Google Maps script yükle - Embed harita için (otomatik yükle)
  useEffect(() => {
    if (activeTab === 'content' && distanceInfo && distanceInfo.rota && 
        distanceInfo.rota.baslangic && distanceInfo.rota.baslangic.enlem && 
        distanceInfo.rota.bitis && distanceInfo.rota.bitis.enlem) {
      
      const baslangicLat = parseFloat(distanceInfo.rota.baslangic.enlem);
      const baslangicLng = parseFloat(distanceInfo.rota.baslangic.boylam);
      const bitisLat = parseFloat(distanceInfo.rota.bitis.enlem);
      const bitisLng = parseFloat(distanceInfo.rota.bitis.boylam);
      
      // Koordinat kontrolü
      if (isNaN(baslangicLat) || isNaN(baslangicLng) || isNaN(bitisLat) || isNaN(bitisLng)) {
        return;
      }
      
      // Harita elementinin render edilmesini bekle
      let retryCount = 0;
      const maxRetries = 30; // 6 saniye (200ms * 30)
      
      const checkAndInitialize = () => {
        const mapElement = document.getElementById('route-map');
        if (!mapElement) {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndInitialize, 200);
          } else {
            // Hata durumunda bile bir placeholder göster
            const placeholder = document.createElement('div');
            placeholder.style.cssText = 'width: 100%; height: 400px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 2px solid #0ea5e9; border-radius: 8px;';
            placeholder.innerHTML = '<div style="text-align: center; color: #666;"><p>Harita yüklenemedi</p><p style="font-size: 12px;">Koordinatlar: ' + baslangicLat.toFixed(6) + ', ' + baslangicLng.toFixed(6) + ' → ' + bitisLat.toFixed(6) + ', ' + bitisLng.toFixed(6) + '</p></div>';
            const parent = document.querySelector('[id="route-map"]')?.parentElement;
            if (parent) {
              parent.appendChild(placeholder);
            }
          }
          return;
        }
        
        // Element bulundu, haritayı başlat
        const initializeMap = () => {
          try {
            // Harita merkezini iki noktanın ortasına al
            const centerLat = (baslangicLat + bitisLat) / 2;
            const centerLng = (baslangicLng + bitisLng) / 2;

            const map = new window.google.maps.Map(mapElement, {
              zoom: 12,
              center: { 
                lat: centerLat, 
                lng: centerLng
              },
              mapTypeId: window.google.maps.MapTypeId.ROADMAP
            });

            // Başlangıç noktası (Teknik Servis)
            const baslangicMarker = new window.google.maps.Marker({
              position: { lat: baslangicLat, lng: baslangicLng },
              map: map,
              title: 'Teknik Servis',
              icon: {
                url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
              },
              label: 'A'
            });

            // Bitiş noktası (Randevu Konumu)
            const bitisMarker = new window.google.maps.Marker({
              position: { lat: bitisLat, lng: bitisLng },
              map: map,
              title: 'Randevu Konumu',
              icon: {
                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
              },
              label: 'B'
            });

            // Info window'lar
            const baslangicInfoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 8px;">
                  <strong>Teknik Servis (Başlangıç)</strong><br/>
                  ${distanceInfo.rota.baslangic.adres || 'Adres bilgisi yok'}<br/>
                  <small>Koordinatlar: ${baslangicLat.toFixed(6)}, ${baslangicLng.toFixed(6)}</small>
                </div>
              `
            });

            const bitisInfoWindow = new window.google.maps.InfoWindow({
              content: `
                <div style="padding: 8px;">
                  <strong>Randevu Konumu (Bitiş)</strong><br/>
                  ${distanceInfo.rota.bitis.adres || 'Adres bilgisi yok'}<br/>
                  <small>Koordinatlar: ${bitisLat.toFixed(6)}, ${bitisLng.toFixed(6)}</small>
                  ${distanceInfo.mesafe ? `<br/><strong>Mesafe: ${formatMesafeKm(distanceInfo.mesafe.kilometre, appointmentContent.mesafeGidisDonus)}</strong>` : ''}
                </div>
              `
            });

            baslangicMarker.addListener('click', () => {
              baslangicInfoWindow.open(map, baslangicMarker);
            });

            bitisMarker.addListener('click', () => {
              bitisInfoWindow.open(map, bitisMarker);
            });

            // Polyline rota çizimi (Routes API'den gelen polyline ile)
            if (appointmentContent.rotaBilgileri?.polyline && window.google.maps.geometry?.encoding) {
              try {
                const decodedPath = window.google.maps.geometry.encoding.decodePath(
                  appointmentContent.rotaBilgileri.polyline
                );
                
                const routePath = new window.google.maps.Polyline({
                  path: decodedPath,
                  geodesic: true,
                  strokeColor: '#0ea5e9',
                  strokeOpacity: 0.8,
                  strokeWeight: 3
                });
                
                routePath.setMap(map);
              } catch (error) {
                // Polyline çizilemedi - sessizce devam et
              }
            }

            // Bitiş marker'ının info window'unu otomatik aç
            bitisInfoWindow.open(map, bitisMarker);
          } catch (error) {
            // Hata durumunda bile koordinatları göster
            mapElement.innerHTML = `
              <div style="width: 100%; height: 100%; background: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 2px solid #0ea5e9; border-radius: 8px;">
                <div style="text-align: center; color: #666; padding: 20px;">
                  <p><strong>Harita yüklenemedi</strong></p>
                  <p style="font-size: 12px; margin-top: 10px;">
                    Başlangıç: ${baslangicLat.toFixed(6)}, ${baslangicLng.toFixed(6)}<br/>
                    Bitiş: ${bitisLat.toFixed(6)}, ${bitisLng.toFixed(6)}
                  </p>
                  <p style="font-size: 11px; margin-top: 10px; color: #999;">Hata: ${error.message}</p>
                </div>
              </div>
            `;
          }
        };

        // Eğer Google Maps zaten yüklüyse direkt kullan
        if (window.google && window.google.maps) {
          setTimeout(initializeMap, 100); // Kısa bir gecikme ile element'in hazır olmasını bekle
          return;
        }

        // Google Maps API script'ini yükle
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          // Script zaten yükleniyor veya yüklü, onload event'ini bekle
          if (window.google && window.google.maps) {
            setTimeout(initializeMap, 100);
          } else {
            existingScript.addEventListener('load', () => {
              setTimeout(initializeMap, 100);
            });
          }
          return;
        }

        const script = document.createElement('script');
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_JS_API_KEY || '';
        if (!apiKey) {
          // API key yoksa bile koordinatları göster
          mapElement.innerHTML = `
            <div style="width: 100%; height: 100%; background: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 2px solid #0ea5e9; border-radius: 8px;">
              <div style="text-align: center; color: #666; padding: 20px;">
                <p><strong>Google Maps API Key bulunamadı</strong></p>
                <p style="font-size: 12px; margin-top: 10px;">
                  Başlangıç: ${baslangicLat.toFixed(6)}, ${baslangicLng.toFixed(6)}<br/>
                  Bitiş: ${bitisLat.toFixed(6)}, ${bitisLng.toFixed(6)}
                </p>
              </div>
            </div>
          `;
          return;
        }

        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&loading=async&callback=initMap`;
        script.async = true;
        script.defer = true;
        
        // Global callback fonksiyonu
        window.initMap = () => {
          setTimeout(initializeMap, 100);
        };
        
        script.onerror = () => {
          // Hata durumunda bile koordinatları göster
          if (mapElement) {
            mapElement.innerHTML = `
              <div style="width: 100%; height: 100%; background: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 2px solid #0ea5e9; border-radius: 8px;">
                <div style="text-align: center; color: #666; padding: 20px;">
                  <p><strong>Google Maps API yüklenemedi</strong></p>
                  <p style="font-size: 12px; margin-top: 10px;">
                    Başlangıç: ${baslangicLat.toFixed(6)}, ${baslangicLng.toFixed(6)}<br/>
                    Bitiş: ${bitisLat.toFixed(6)}, ${bitisLng.toFixed(6)}
                  </p>
                </div>
              </div>
            `;
          }
        };
        document.head.appendChild(script);
      };
      
      // İlk kontrolü başlat
      setTimeout(checkAndInitialize, 500); // 500ms gecikme ile element'in render edilmesini bekle

      return () => {
        // Cleanup - harita elementini temizle
        const mapElement = document.getElementById('route-map');
        if (mapElement) {
          mapElement.innerHTML = '';
        }
        // Global callback'i temizle
        if (window.initMap) {
          delete window.initMap;
        }
      };
    }
  }, [activeTab, distanceInfo, appointmentContent]);

  // İlleri yükle
  useEffect(() => {
    const loadCities = async () => {
      try {
        const response = await fetchCities();
        const citiesList = response?.veri?.iller || response?.veri?.cities || response?.veri || response?.data || [];
        setCities(Array.isArray(citiesList) ? citiesList : []);
      } catch (err) {
        setCities([]);
      }
    };
    loadCities();
  }, []);

  // İlçeleri yükle
  useEffect(() => {
    if (!selectedCityId || selectedCityId.trim() === '') {
      setDistricts([]);
      return;
    }
    
    const loadDistricts = async () => {
      try {
        // CityId'yi number'a çevir (API bazen string bazen number bekliyor)
        const cityIdNum = Number(selectedCityId) || selectedCityId;
        const response = await fetchDistrictsByCity(cityIdNum);
        let districtsList = response?.veri?.ilceler || response?.veri?.districts || response?.veri || response?.data || [];
        if (!Array.isArray(districtsList)) {
          districtsList = [];
        }
        setDistricts(districtsList);
      } catch (err) {
        setDistricts([]);
      }
    };
    loadDistricts();
  }, [selectedCityId]);

  // Teknik servisleri yükle (İl seçildiğinde veya ilçe seçildiğinde)
  const loadTechServices = useCallback(async () => {
    if (!selectedCityId) {
      setTechServices([]);
      return;
    }

    setTechServicesLoading(true);
    try {
      // CityId'yi number'a çevir
      const cityIdNum = Number(selectedCityId) || selectedCityId;
      
      // Eğer ilçe seçildiyse, hem il hem ilçe bazında filtrele
      // Eğer sadece il seçildiyse, sadece il bazında filtrele
      const params = {
        cityId: cityIdNum,
        aktif: true,
        page: 1,
        limit: 100 // Tüm servisleri getirmek için
      };
      
      // İlçe seçildiyse districtId ekle
      if (selectedDistrictId && selectedDistrictId.trim() !== '') {
        const districtIdNum = Number(selectedDistrictId) || selectedDistrictId;
        params.districtId = districtIdNum;
      }
      
      const response = await fetchTechServices(params);
      
      const services = response?.veri?.teknikServisler || 
                       response?.veri?.data?.teknikServisler ||
                       response?.veri || 
                       response?.data || 
                       [];
      setTechServices(Array.isArray(services) ? services : []);
      } catch (err) {
      setTechServices([]);
    } finally {
      setTechServicesLoading(false);
    }
  }, [selectedCityId, selectedDistrictId]);

  useEffect(() => {
    loadTechServices();
  }, [loadTechServices]);

  // Teknik servis ataması yap
  const handleAssignTechService = async () => {
    if (!selectedTechServiceId) {
      if (window.showNotification) {
        window.showNotification('error', 'Lütfen bir teknik servis seçin');
      }
      return;
    }

    setSubmitting(true);
    try {
      const response = await assignTechServiceToAppointment(randevuId, selectedTechServiceId);
      
      if (response?.basarili === false) {
        throw new Error(response?.mesaj || 'Teknik servis ataması yapılamadı');
      }

      if (window.showNotification) {
        window.showNotification('success', 'Teknik servis başarıyla atandı');
      }

      // Randevu detayını yeniden yükle
      await loadAppointmentDetail();
      setSelectedTechServiceId('');
      setSelectedCityId('');
      setSelectedDistrictId('');
      } catch (err) {
      const errorMessage = getErrorMessage(err, 'Teknik servis ataması yapılamadı');
      
      // 404 hatası için özel mesaj
      if (err?.status === 404 || errorMessage.includes('Sayfa bulunamadı') || errorMessage.includes('endpoint')) {
        if (window.showNotification) {
          window.showNotification('error', 'Teknik servis atama endpoint\'i bulunamadı. Backend\'de bu endpoint henüz implement edilmemiş olabilir. Lütfen Prompt#36\'yı backend ekibine iletin.');
        }
      } else {
        if (window.showNotification) {
          window.showNotification('error', errorMessage);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="main-content">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Randevu detayı yükleniyor</div>
        </div>
      </main>
    );
  }

  if (error || !appointment) {
    return (
      <main className="main-content">
        <div className="content-header">
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
        </div>
        <div className="error-state">{safeText(error || 'Randevu bulunamadı')}</div>
      </main>
    );
  }

  const appointmentDate = appointment.randevuTarihi 
    ? new Date(appointment.randevuTarihi).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '---';
  
  const appointmentTime = appointment.randevuSaati || appointment.randevuSaatAraligi || '---';
  const isTechServiceAssignmentPending = appointmentStatus === 'Teknik Servis Ataması Bekliyor' || 
                                          appointmentStatus === 'Randevu Tarihi Bekleniyor';

  const statusToken = (() => {
    const value = String(appointmentStatus || '').toLowerCase();
    if (value.includes('onaylandı') || value.includes('tamamlandı')) return 'done';
    if (value.includes('iptal')) return 'cancel';
    if (value.includes('bekliyor') || value.includes('bekleniyor')) return 'progress';
    return 'new';
  })();

  // Teknisyen bilgisini önce appointment'tan, yoksa atama geçmişinden al
  const getTechnicianName = () => {
    // Önce appointment.teknisyen'den dene
    if (appointment.teknisyen) {
      // Backend'den gelen teknisyenAdi field'ını kontrol et
      if (appointment.teknisyen.teknisyenAdi) {
        return appointment.teknisyen.teknisyenAdi;
      }
      
      // Populate edilmiş teknisyen bilgisini kontrol et
      const ad = appointment.teknisyen.ad || appointment.teknisyen.name || appointment.teknisyen.adi || '';
      const soyad = appointment.teknisyen.soyad || appointment.teknisyen.surname || appointment.teknisyen.soyadi || '';
      const fullName = `${ad} ${soyad}`.trim();
      if (fullName) return fullName;
    }
    
    // Atama geçmişinden teknisyen bilgisini al
    // NOT: Sadece iptal edilmemiş teknisyenler gösterilir
    // En son "Teknisyen" ataması var mı ve sonrasında iptal var mı kontrol et
    
    // Tüm teknisyen atamalarını bul
    const allTechnicianAssignments = assignmentHistory.filter(
      (history) => history.atamaTipi === 'Teknisyen' || history.atamaTipi === 'Teknisyen Ataması'
    );
    
    // Tüm teknisyen iptallerini bul
    const allTechnicianCancellations = assignmentHistory.filter(
      (history) => history.atamaTipi === 'Teknisyen İptali'
    );
    
    // Eğer iptal sayısı atama sayısına eşit veya fazlaysa, aktif teknisyen yok
    if (allTechnicianCancellations.length >= allTechnicianAssignments.length) {
      return null;
    }
    
    // En son teknisyen atamasını al
    const lastTechnicianAssignment = allTechnicianAssignments[allTechnicianAssignments.length - 1];
    
    if (lastTechnicianAssignment?.atananAdi && lastTechnicianAssignment.durum === 'Atandı') {
      return lastTechnicianAssignment.atananAdi;
    }
    
    // Eğer teknisyenId varsa ama populate edilmemişse
    if (appointment.teknisyenId) {
      return 'Atandı (Detay yüklenemedi)';
    }
    
    return null;
  };

  const technicianName = getTechnicianName();
  // Sadece teknisyen adı varsa göster (iptal edilmişleri gösterme)
  const hasTechnician = technicianName !== null;

  const hasTeknikServis = appointment.teknikServis || appointment.teknikServisId;
  const teknikServisAdi = hasTeknikServis ? getTeknikServisDisplayName(appointment) : null;
  const isRandevuTamamlandi = appointment.durum === 'Randevu Tamamlandı';
  const bedelDurumMeta = getRandevuBedeliDurumMeta(randevuBedeliDurum);
  const showBedelDurumChip = bedelDurumMeta && !randevuBedeliAltEtiket;
  const odemeDurumuGosterim = getOdemeDurumuGosterim(appointment);
  const otpSkipped = appointment?.otpAtlandi === true
    || randevuFormuMeta?.otpAtlandi === true
    || randevuFormuMeta?.otpDogrulandi === false;
  const showIptalBilgileri =
    appointment.durum === 'Randevu İptal Edildi'
    || appointment.iptalNedeni
    || appointment.iptalEden
    || appointment.iptalEdenIsim;

  const serviceRecord = appointment.servisKaydi || {};
  const resolvedServisKaydiId =
    servisKaydiId
    || appointment?.servisKaydiId
    || serviceRecord?.id
    || serviceRecord?._id;
  const isKurulumIslem =
    Boolean(serviceRecord?.islemTipi) &&
    String(serviceRecord.islemTipi).toLowerCase().includes('kurulum');
  const customer = serviceRecord?.musteri || {};
  const customerName = `${customer.adi || customer.name || ''} ${customer.soyadi || customer.surname || ''}`.trim() || '---';

  return (
    <main className="main-content service-record-update">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Randevu Detayı</h1>
        </div>
      </div>

      <div className="status-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'record' ? 'active' : ''}`}
          onClick={() => setActiveTab('record')}
        >
          Randevu Bilgileri
        </button>
        {appointmentStatus === 'Randevu Tamamlandı' && (
          <button
            type="button"
            className={`status-tab-btn ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            Randevu İçeriği
          </button>
        )}
        <button
          type="button"
          className={`status-tab-btn ${activeTab === 'detail' ? 'active' : ''}`}
          onClick={() => setActiveTab('detail')}
        >
          Detay
        </button>
      </div>

      {activeTab === 'detail' && (
        detailedLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#007bff' }}></i>
            <p style={{ marginTop: '16px', color: '#666' }}>Detaylı bilgiler yükleniyor...</p>
          </div>
        ) : (
          (() => {
            const finalAppointment = detailedData?.randevu || appointment;
            const finalServiceRecord = detailedData?.servisKaydi || appointment?.servisKaydi || null;
            
            return (
              <AppointmentFullDetailView 
                appointment={finalAppointment}
                serviceRecord={finalServiceRecord}
                assignmentHistory={assignmentHistory}
              />
            );
          })()
        )
      )}

      {activeTab === 'record' && (
        <div className="service-record-info randevu-bilgi-panel">
          <div className="randevu-bilgi-hero">
            <div className="randevu-bilgi-hero__datetime">
              <span className="randevu-bilgi-hero__item">
                <i className="fas fa-calendar-alt" aria-hidden />
                {appointmentDate}
              </span>
              <span className="randevu-bilgi-hero__item">
                <i className="fas fa-clock" aria-hidden />
                {appointmentTime}
              </span>
            </div>
            <span className={`status-chip status-${statusToken}`}>
              {appointmentStatus}
            </span>
          </div>

          <div className="randevu-bilgi-body">
            <ServisKaydiBaglantiKart
              servisKaydiId={resolvedServisKaydiId ? String(resolvedServisKaydiId) : null}
              serviceRecord={serviceRecord}
            />

            {(hasTeknikServis || hasTechnician || isRandevuTamamlandi) && (
              <div className="randevu-bilgi-grid">
                {hasTeknikServis && (
                  <div className="randevu-bilgi-card">
                    <div className="randevu-bilgi-card__label">Atanan Teknik Servis</div>
                    <div className="randevu-bilgi-card__value">{teknikServisAdi}</div>
                  </div>
                )}

                {hasTechnician && (
                  <div className="randevu-bilgi-card">
                    <div className="randevu-bilgi-card__label">Atanan Teknisyen</div>
                    <div className="randevu-bilgi-card__value">{technicianName || 'Atanmadı'}</div>
                  </div>
                )}

                {isRandevuTamamlandi && (
                  <div className="randevu-bilgi-card">
                    <div className="randevu-bilgi-card__head">
                      <div className="randevu-bilgi-card__label">Randevu Bedeli</div>
                      {!randevuBedeliLoading && showBedelDurumChip && (
                        <span className={`status-chip status-chip--sm ${bedelDurumMeta.chipClass}`}>
                          {bedelDurumMeta.label}
                        </span>
                      )}
                    </div>
                    <div className="randevu-bilgi-card__value randevu-bilgi-card__value--amount">
                      {randevuBedeliLoading
                        ? 'Yükleniyor…'
                        : randevuBedeliTutar != null
                          ? `${randevuBedeliTutar.toFixed(2)} ₺`
                          : '—'}
                    </div>
                    {randevuBedeliAltEtiket && !randevuBedeliLoading ? (
                      <div className="randevu-bilgi-card__hint" style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        {randevuBedeliAltEtiket}
                      </div>
                    ) : null}
                    {odemeDurumuGosterim && !randevuBedeliLoading ? (
                      <div style={{ marginTop: '6px' }}>
                        <div
                          className={`randevu-bilgi-card__hint randevu-bilgi-card__hint--odeme randevu-bilgi-card__hint--${odemeDurumuGosterim.tone}`}
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: odemeDurumuGosterim.tone === 'success' ? '#166534' : '#991b1b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <i className={`fas ${odemeDurumuGosterim.icon}`} />
                          {odemeDurumuGosterim.label}
                        </div>
                        {odemeDurumuGosterim.subLabel ? (
                          <div
                            style={{
                              fontSize: '11px',
                              color: '#64748b',
                              marginTop: '4px',
                              lineHeight: 1.45,
                            }}
                          >
                            {odemeDurumuGosterim.subLabel}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {appointment.aciklama && (
              <div className="randevu-bilgi-note">
                <div className="randevu-bilgi-note__label">Açıklama</div>
                <div className="randevu-bilgi-note__text">{appointment.aciklama}</div>
              </div>
            )}

            {isRandevuTamamlandi && appointment.tamamlamaBilgileri?.tamamlanmaTarihi && (
              <div className="randevu-bilgi-note">
                <div className="randevu-bilgi-note__label">Tamamlanma</div>
                <div className="randevu-bilgi-note__text">
                  {new Date(appointment.tamamlamaBilgileri.tamamlanmaTarihi).toLocaleString('tr-TR')}
                  {appointment.tamamlamaBilgileri.sonrakiIslem
                    ? ` · ${appointment.tamamlamaBilgileri.sonrakiIslem}`
                    : ''}
                </div>
              </div>
            )}

            {isRandevuTamamlandi && (appointment.konumAtlandi || appointment.otpAtlandi || appointment.offlineTamamlama || odemeDurumuGosterim?.tone === 'danger') && (
              <div className="randevu-bilgi-note randevu-bilgi-note--warning">
                <div className="randevu-bilgi-note__label">Tamamlama uyarıları</div>
                <div className="randevu-bilgi-note__text">
                  {odemeDurumuGosterim?.tone === 'danger' ? (
                    <div><i className="fas fa-money-bill-wave" /> Müşteri tahsilatı alınamadı — ödeme problemli randevu listesinde izlenebilir.</div>
                  ) : null}
                  {appointment.konumAtlandi ? (
                    <div><i className="fas fa-location-crosshairs" /> Konum alınamadı — tamamlama konumsuz yapıldı.</div>
                  ) : null}
                  {appointment.otpAtlandi ? (
                    <div><i className="fas fa-sms" /> OTP Doğrulaması yapılmadı.</div>
                  ) : null}
                  {appointment.offlineTamamlama ? (
                    <div>
                      <i className="fas fa-cloud-upload-alt" /> Cihazda tamamlandı
                      {appointment.tamamlamaSenkronTarihi
                        ? ` · sunucuya ${new Date(appointment.tamamlamaSenkronTarihi).toLocaleString('tr-TR')}`
                        : ''}
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {isRandevuTamamlandi && isKurulumIslem && appointment.ilkCalistirmaYapildi !== null && appointment.ilkCalistirmaYapildi !== undefined && (
              <div className="randevu-bilgi-note">
                <div className="randevu-bilgi-note__label">İlk çalıştırma</div>
                <div className="randevu-bilgi-note__text randevu-bilgi-ilk-calistirma">
                  <span className={appointment.ilkCalistirmaYapildi ? 'is-active' : 'is-muted'}>
                    {appointment.ilkCalistirmaYapildi ? '●' : '○'} Yapıldı
                  </span>
                  <span className="randevu-bilgi-ilk-calistirma__sep">|</span>
                  <span className={!appointment.ilkCalistirmaYapildi ? 'is-active' : 'is-muted'}>
                    {!appointment.ilkCalistirmaYapildi ? '●' : '○'} Yapılamadı
                  </span>
                </div>
              </div>
            )}

            {isRandevuTamamlandi && (
              <div className="randevu-bilgi-actions">
                <div className={`randevu-bilgi-action-card${otpSkipped ? ' randevu-bilgi-action-card--otp-warning' : ''}`}>
                  <div className="randevu-bilgi-action-card__title">
                    <i className="fas fa-file-pdf" aria-hidden />
                    Randevu Formu (PDF)
                  </div>
                  {randevuFormuLoading ? (
                    <span className="randevu-bilgi-action-card__hint">Yükleniyor…</span>
                  ) : (
                    <>
                      {otpSkipped ? (
                        <div className="randevu-bilgi-otp-warning" role="status">
                          <i className="fas fa-sms" aria-hidden />
                          <span>OTP Doğrulaması yapılmadı.</span>
                        </div>
                      ) : randevuFormuMeta?.otpDogrulamaMetni ? (
                        <p className="randevu-bilgi-action-card__hint randevu-bilgi-action-card__hint--otp-ok">
                          {randevuFormuMeta.otpDogrulamaMetni}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={handleOpenRandevuFormuPdf}
                        disabled={randevuFormuPdfLoading}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        <i className="fas fa-file-pdf" aria-hidden />
                        {randevuFormuPdfLoading ? 'Açılıyor…' : 'Formu Görüntüle / Yazdır'}
                      </button>
                    </>
                  )}
                </div>

                <div className="randevu-bilgi-action-card">
                  <div className="randevu-bilgi-action-card__title">
                    <i className="fas fa-link" aria-hidden />
                    Müşteriye Gönderilen Detay Raporu
                  </div>
                  {detayLinkLoading && (
                    <span className="randevu-bilgi-action-card__hint">Yükleniyor…</span>
                  )}
                  {detayLinkError && !detayLinkLoading && (
                    <span className="randevu-bilgi-action-card__hint">{detayLinkError}</span>
                  )}
                  {detayLink && (
                    <a
                      href={detayLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="randevu-bilgi-action-card__link"
                    >
                      {detayLink}
                    </a>
                  )}
                </div>
              </div>
            )}

            {showIptalBilgileri && (
              <div className="randevu-bilgi-alert">
                <div className="randevu-bilgi-alert__title">İptal Bilgileri</div>
                <div className="randevu-bilgi-alert__rows">
                  {appointment.iptalNedeni && (
                    <div>
                      <strong>İptal Nedeni:</strong> {appointment.iptalNedeni}
                    </div>
                  )}
                  {(appointment.iptalEdenIsim || appointment.iptalEden) && (
                    <div>
                      <strong>İptal Eden:</strong> {appointment.iptalEdenIsim || appointment.iptalEden || '—'}
                    </div>
                  )}
                  {appointment.iptalTarihi && (
                    <div>
                      <strong>İptal Tarihi:</strong>{' '}
                      {new Date(appointment.iptalTarihi).toLocaleString('tr-TR')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {isTechServiceAssignmentPending && (
            <div className="randevu-bilgi-subpanel">
              <div className="randevu-bilgi-subpanel__title">Teknik Servis Ataması</div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAssignTechService();
                }}
              >
                <div className="info-row" style={{ marginBottom: '20px' }}>
                  <div className="info-item">
                    <label className="info-label">
                      İl <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      value={selectedCityId}
                      onChange={(e) => {
                        const newCityId = e.target.value;
                        setSelectedCityId(newCityId);
                        setSelectedDistrictId('');
                        setSelectedTechServiceId('');
                      }}
                      required
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        marginTop: '4px',
                        background: 'var(--bg-white)',
                        color: 'var(--text-dark)',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">İl Seçin</option>
                      {cities.map((city) => (
                        <option key={city.id || city._id} value={city.id || city._id}>
                          {city.name || city.ad || city.adi}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="info-item">
                    <label className="info-label">İlçe</label>
                    <select
                      value={selectedDistrictId}
                      onChange={(e) => {
                        const newDistrictId = e.target.value;
                        setSelectedDistrictId(newDistrictId);
                        setSelectedTechServiceId('');
                      }}
                      disabled={!selectedCityId}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        marginTop: '4px',
                        background: selectedCityId ? 'var(--bg-white)' : '#f8fafc',
                        color: selectedCityId ? 'var(--text-dark)' : 'var(--text-gray)',
                        cursor: selectedCityId ? 'pointer' : 'not-allowed',
                        opacity: selectedCityId ? 1 : 0.6,
                      }}
                    >
                      <option value="">İlçe Seçin</option>
                      {districts.map((district) => (
                        <option key={district.id || district._id} value={district.id || district._id}>
                          {district.name || district.ad || district.adi}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="info-item" style={{ marginBottom: '20px' }}>
                  <label className="info-label">
                    Teknik Servis <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={selectedTechServiceId}
                    onChange={(e) => setSelectedTechServiceId(e.target.value)}
                    disabled={!selectedCityId || techServicesLoading}
                    required
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      marginTop: '4px',
                      background: selectedCityId ? 'var(--bg-white)' : '#f8fafc',
                      color: selectedCityId ? 'var(--text-dark)' : 'var(--text-gray)',
                      cursor: selectedCityId ? 'pointer' : 'not-allowed',
                      opacity: selectedCityId ? 1 : 0.6,
                    }}
                  >
                    <option value="">Teknik Servis Seçin</option>
                    {techServicesLoading ? (
                      <option disabled>Yükleniyor...</option>
                    ) : (
                      techServices.map((service) => (
                        <option key={service.id || service._id} value={service.id || service._id}>
                          {service.ad || service.name} {service.kod ? `(${service.kod})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                  {techServices.length === 0 && selectedCityId && !techServicesLoading && (
                    <div className="info-meta" style={{ marginTop: '4px', color: '#ef4444' }}>
                      {selectedDistrictId
                        ? 'Bu ilçede teknik servis bulunamadı'
                        : 'Bu ilde teknik servis bulunamadı'}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      setSelectedTechServiceId('');
                      setSelectedCityId('');
                      setSelectedDistrictId('');
                    }}
                    disabled={submitting}
                  >
                    <i className="fas fa-times"></i>
                    <span>Temizle</span>
                  </button>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={!selectedTechServiceId || submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Atanıyor...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        <span>Teknik Servis Ata</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}


      {activeTab === 'content' && (
        <div>
          {contentLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <div className="loading-text">Randevu içeriği yükleniyor</div>
            </div>
          ) : appointmentContent ? (
            <div className="service-record-info">
              {/* Garanti Dahilinde - En Üstte */}
              {/* Teknisyen Notu */}
              <div className="info-section" style={{ marginBottom: '24px' }}>
                <div className="info-section__label" style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                  Müşteri için teknisyen notu
                </div>
                <div className="info-section__value" style={{ whiteSpace: 'pre-wrap' }}>
                  {appointmentContent.musteriIcinTeknisyenNotu && appointmentContent.musteriIcinTeknisyenNotu.trim()
                    ? appointmentContent.musteriIcinTeknisyenNotu
                    : '—'}
                </div>
              </div>
              <div className="info-section" style={{ marginBottom: '24px' }}>
                <div className="info-section__label" style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>
                  Teknisyen notu (iç)
                </div>
                <div className="info-section__value" style={{ whiteSpace: 'pre-wrap' }}>
                  {appointmentContent.teknisyenNotu && appointmentContent.teknisyenNotu.trim()
                    ? appointmentContent.teknisyenNotu
                    : '—'}
                </div>
              </div>

              {appointmentContent.garantiDahilinde !== null && appointmentContent.garantiDahilinde !== undefined && (
                <div className="info-section" style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '16px',
                    background: appointmentContent.garantiDahilinde ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: `2px solid ${appointmentContent.garantiDahilinde ? '#22c55e' : '#ef4444'}`
                  }}>
                    <div style={{ fontSize: '24px' }}>
                      {appointmentContent.garantiDahilinde ? (
                        <i className="fas fa-check-circle" style={{ color: '#22c55e' }}></i>
                      ) : (
                        <i className="fas fa-times-circle" style={{ color: '#ef4444' }}></i>
                      )}
                    </div>
                    <div>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: 600,
                        color: appointmentContent.garantiDahilinde ? '#166534' : '#991b1b',
                        marginBottom: '4px'
                      }}>
                        Garanti Dahilinde
                      </div>
                      <div style={{ 
                        fontSize: '14px',
                        color: appointmentContent.garantiDahilinde ? '#166534' : '#991b1b',
                        opacity: 0.8
                      }}>
                        {appointmentContent.garantiDahilinde ? 'Bu randevu garanti kapsamındadır' : 'Bu randevu garanti kapsamında değildir'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(appointmentContent.garantiSartlariSaglanmadi) && (
                <div className="info-section" style={{ marginBottom: '24px' }}>
                  <div style={{
                    padding: '14px 16px',
                    borderRadius: '8px',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    color: '#92400e',
                    fontSize: '14px',
                    lineHeight: 1.5,
                  }}>
                    <strong>Garanti şartları sağlanmadı.</strong> İşlem garanti kapsamı dışında tamamlanmıştır.
                    {appointmentContent.servisBedeli != null && (
                      <span style={{ display: 'block', marginTop: '6px' }}>
                        Randevu bedeli (servis bedeli): {Number(appointmentContent.servisBedeli).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </span>
                    )}
                  </div>
                </div>
              )}

              {(appointmentContent.odemeDurumu || appointmentContent.odemeProblemli || appointmentContent.odemeBeyanTahsilati) && (
                <div className="info-section" style={{ marginBottom: '24px' }}>
                  {(() => {
                    const odemeGosterim = resolveOdemeDurumuGosterim(appointmentContent);
                    const isOk = odemeGosterim?.tone === 'success';
                    return (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    background: isOk ? '#f0fdf4' : '#fef2f2',
                    borderRadius: '8px',
                    border: `2px solid ${isOk ? '#22c55e' : '#ef4444'}`,
                  }}>
                    <div style={{ fontSize: '24px' }}>
                      <i className={`fas ${isOk ? 'fa-check-circle' : 'fa-exclamation-circle'}`}
                        style={{ color: isOk ? '#22c55e' : '#ef4444' }}></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: isOk ? '#166534' : '#991b1b' }}>
                        Müşteri Tahsilatı: {odemeGosterim?.label || (appointmentContent.odemeDurumu === 'alindi' ? 'Alındı' : 'Alınamadı')}
                      </div>
                      {odemeGosterim?.subLabel ? (
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '6px', lineHeight: 1.45 }}>
                          {odemeGosterim.subLabel}
                        </div>
                      ) : null}
                      {appointmentContent.musteriTahsilatToplam != null && (
                        <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
                          Tutar: {Number(appointmentContent.musteriTahsilatToplam).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </div>
                      )}
                      {appointmentContent.odemeProblemli && (
                        <div style={{ fontSize: '13px', color: '#991b1b', marginTop: '4px' }}>
                          Ödeme problemli randevu listesinde
                        </div>
                      )}
                    </div>
                  </div>
                    );
                  })()}
                </div>
              )}

              {/* Yapılan İşlemler */}
              <div className="info-section">
                <div className="info-section__label" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                  Yapılan İşlemler
                </div>
                {appointmentContent.yapilanIslemler && appointmentContent.yapilanIslemler.length > 0 ? (
                  <div>
                    <div className="table-container">
                      <table className="admins-table">
                        <thead>
                          <tr>
                            <th>İşlem Adı</th>
                            <th>İşlem Kodu</th>
                            <th>Adet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appointmentContent.yapilanIslemler.map((islem, index) => {
                            const adet = islem.adet || 1;
                            
                            return (
                              <tr key={islem._id || islem.id || index}>
                                <td>{islem.islemAdi || islem.ad || '—'}</td>
                                <td>{islem.islemKodu || '—'}</td>
                                <td>{adet}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="placeholder-text">Yapılan işlem bulunamadı</div>
                )}
              </div>

              {/* Değiştirilen Parçalar */}
              <div className="info-section" style={{ marginTop: '24px' }}>
                <div className="info-section__label" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                  Değiştirilen Parçalar
                </div>
                {appointmentContent.degistirilenParcalar && appointmentContent.degistirilenParcalar.length > 0 ? (
                  <div>
                    <div className="table-container">
                      <table className="admins-table">
                        <thead>
                          <tr>
                            <th>Parça Adı</th>
                            <th>Parça Kodu</th>
                            <th>Miktar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appointmentContent.degistirilenParcalar.map((parca, index) => {
                            const parcaAdi = parca.parcaAdi || parca.ad || '—';
                            const parcaKodu = parca.parcaKodu || parca.kod || '—';
                            const miktar = parca.adet || parca.miktar || 1;
                            
                            return (
                              <tr key={parca._id || parca.id || index}>
                                <td>{parcaAdi}</td>
                                <td>{parcaKodu}</td>
                                <td>{miktar}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="placeholder-text">Değiştirilen parça bulunamadı</div>
                )}
              </div>

              {/* Randevu Fotoları */}
              {appointmentContent.photos && appointmentContent.photos.length > 0 && (
                <div className="info-section" style={{ marginTop: '24px' }}>
                  <div className="info-section__label" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                    Randevu Fotoları
                  </div>
                  <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                      {appointmentContent.photos.map((photo, index) => {
                        const photoUrl = absoluteUploadUrl(photo.url);
                        
                        return (
                          <div
                            key={photo._id || photo.url || index}
                            onClick={() => {
                              setSelectedPhoto({ ...photo, index });
                              setPhotoModalOpen(true);
                            }}
                            style={{
                              position: 'relative',
                              width: '100%',
                              paddingTop: '100%', // 1:1 aspect ratio
                              cursor: 'pointer',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '2px solid #e0e0e0',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#0ea5e9';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e0e0e0';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <img
                              src={photoUrl}
                              alt={photo.dosyaAdi || `Fotoğraf ${index + 1}`}
                              crossOrigin="anonymous"
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect width="120" height="120" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EYüklenemedi%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            {photo.yuklenmeTarihi && (
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                                color: '#ffffff',
                                fontSize: '10px',
                                padding: '4px 6px',
                                textAlign: 'center',
                              }}>
                                {new Date(photo.yuklenmeTarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                              </div>
                            )}
                            {photo.kaynak ? (
                              <div style={{
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                background: 'rgba(15, 23, 42, 0.78)',
                                color: '#fff',
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '999px',
                              }}>
                                {photo.kaynak === 'galeri' ? 'Galeri' : 'Kamera'}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Mesafe Bilgileri ve Rota - Birleştirilmiş Bölüm */}
              {appointmentContent.mesafeKm !== null && appointmentContent.mesafeKm !== undefined && distanceInfo && distanceInfo.rota && (
                <div className="info-section" style={{ marginTop: '24px' }}>
                  <div className="info-section__label" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                    Mesafe Bilgileri ve Rota
                  </div>
                  <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                    {/* Harita - Başlığın hemen altında */}
                    {distanceInfo.rota.baslangic.enlem && distanceInfo.rota.bitis.enlem && (
                      <div style={{ marginBottom: '16px' }}>
                        <div id="route-map" style={{ width: '100%', height: '400px', borderRadius: '8px', border: '2px solid #0ea5e9' }}></div>
                      </div>
                    )}

                    {/* Tamamlanma Adresi */}
                    {appointmentContent.tamamlanmaAdresi && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px', color: '#0c4a6e' }}>Tamamlanma Adresi</div>
                        <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                          {appointmentContent.tamamlanmaAdresi}
                        </div>
                      </div>
                    )}

                    {/* Tamamlanma Konum Bilgileri (ufak ve hafif silik) */}
                    {appointmentContent.konumMuhru && appointmentContent.konumMuhru.enlem && appointmentContent.konumMuhru.boylam && (
                      <div style={{ marginBottom: '16px', fontSize: '12px', color: '#999', opacity: 0.7 }}>
                        <div style={{ marginBottom: '2px' }}>
                          <strong>Koordinatlar:</strong> {appointmentContent.konumMuhru.enlem.toFixed(6)}, {appointmentContent.konumMuhru.boylam.toFixed(6)}
                        </div>
                        {appointmentContent.konumMuhru.tarih && (
                          <div>
                            <strong>Tamamlanma Tarihi:</strong>{' '}
                            {new Date(appointmentContent.konumMuhru.tarih).toLocaleString('tr-TR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mesafe */}
                    {appointmentContent.mesafeKm > 0 && (
                      <div style={{ padding: '12px', background: '#ffffff', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Mesafe: {formatMesafeKm(appointmentContent.mesafeKm, appointmentContent.mesafeGidisDonus)}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tamamlanma konumu — rota haritası yoksa tek nokta Google Maps */}
              {appointmentContent.konumMuhru?.enlem
                && appointmentContent.konumMuhru?.boylam
                && !(appointmentContent.mesafeKm !== null
                  && appointmentContent.mesafeKm !== undefined
                  && distanceInfo?.rota) && (
                <div className="info-section" style={{ marginTop: '24px' }}>
                  <div className="info-section__label" style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
                    Tamamlanma Konumu
                  </div>
                  <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                    {appointmentContent.tamamlanmaAdresi && (
                      <div style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.5' }}>
                        {appointmentContent.tamamlanmaAdresi}
                      </div>
                    )}
                    <AppointmentCompletionMap appointment={appointment} height={400} />
                  </div>
                </div>
              )}


            </div>
          ) : (
            <div className="error-state">Randevu içeriği yüklenemedi veya içerik bulunamadı</div>
          )}
        </div>
      )}

      {/* Teknisyen Seçim Modal'ı (Prompt#43) */}
      <TechnicianSelectModal
        isOpen={showTechnicianModal}
        onClose={() => setShowTechnicianModal(false)}
        onSubmit={handleTechnicianSubmit}
        appointmentDate={appointment?.randevuTarihi}
        appointmentTime={appointment?.randevuSaati}
        techServiceId={appointment?.teknikServisId || appointment?.teknikServis?._id || appointment?.teknikServis?.id}
      />

      {/* Fotoğraf Modal */}
      {photoModalOpen && selectedPhoto && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => {
            setPhotoModalOpen(false);
            setSelectedPhoto(null);
          }}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#ffffff',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                setPhotoModalOpen(false);
                setSelectedPhoto(null);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                fontWeight: 'bold',
              }}
            >
              ×
            </button>
            <img
              src={absoluteUploadUrl(selectedPhoto.url) || undefined}
              alt={selectedPhoto.dosyaAdi || 'Fotoğraf'}
              crossOrigin="anonymous"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'block',
                objectFit: 'contain',
              }}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect width="800" height="600" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="24"%3EFotoğraf yüklenemedi%3C/text%3E%3C/svg%3E';
              }}
            />
            <div style={{
              padding: '12px 20px',
              background: '#ffffff',
              borderTop: '1px solid #e0e0e0',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                {selectedPhoto.dosyaAdi || 'Fotoğraf'}
              </div>
              {selectedPhoto.yuklenmeTarihi && (
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Yüklenme: {new Date(selectedPhoto.yuklenmeTarihi).toLocaleString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AppointmentDetailPage;
